import React, { useState } from "react";
import { useFela } from "react-fela";

import { useTheme } from "@/hooks/useTheme";
import { Grid } from "@/components/Grid";
import { Heading } from "@/components/Heading";
import { Image } from "@/components/Image";
import { LinkButton } from "@/components/LinkButton";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { Icon, StyledIconWrapper } from "@/components/Icon";
import { IFigmaBackupTopScreen } from "./types";

type IProps = IFigmaBackupTopScreen & JSX.IntrinsicElements["div"];

export const FigmaBackupTopScreen: React.FC<IProps> = ({
  title,
  subtitle,
  src,
  buttonLabel,
  buttonSecondaryLabel,
  onButtonClick,
  showVideo = true,
  ...rest
}) => {
  const { css } = useFela();
  const { bp, colors } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const buttonStyles = {
    width: "auto",
    padding: "0 26px",
    marginRight: 24,
    boxShadow: "0 18px 54px 0 rgba(255, 111, 2, 0.5)",
  };

  return (
    <Grid
      id="DashboardSection"
      customStyle={{
        position: "relative",
        flexDirection: "column",
        paddingBottom: 0,
        [bp.FROM_DESKTOP]: {
          paddingBottom: showVideo ? 0 : "auto",
        },
      }}
      wrapperStyle={{
        background: `center / cover no-repeat #fffffe url('/images/semi-circle-shape.png')`,
        [bp.FROM_TABLET]: {},
        [bp.FROM_LARGE_DESKTOP]: {
          backgroundPositionY: -200,
        },
      }}
      {...rest}
    >
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        })}
      >
        <p
          className={css({
            marginBottom: 12,
            fontSize: 14,
            lineHeight: "26px",
            letterSpacing: ".64px",
            fontWeight: 600,
            color: colors.DARK_GREY,
          })}
        >
          {subtitle}
        </p>
        <Heading
          as="h1"
          customStyle={{
            fontSize: 36,
            lineHeight: 1.2,
            marginBottom: 30,
            letterSpacing: "-0.01rem",
            color: colors.DARK_GREY,
            [bp.FROM_TABLET]: {
              maxWidth: 850,
              fontSize: 48,
              lineHeight: "60px",
              marginBottom: 40,
            },
          }}
        >
          {title}
        </Heading>
        <div
          className={css({
            display: "flex",
            justifyContent: "center",
            marginBottom: 30,
          })}
        >
          <Button
            id="ACTION#1"
            onClick={onButtonClick}
            customStyle={buttonStyles}
          >
            {buttonLabel}
          </Button>
          {buttonSecondaryLabel && (
            <LinkButton
              title={buttonSecondaryLabel}
              customStyle={{
                width: "fit-content",
                backgroundColor: "transparent",
                color: colors.ORANGE,
              }}
              scrollToId="PricingSection"
            />
          )}
        </div>
        <div
          className={css({
            width: "100%",
            marginBottom: 20,
            [bp.FROM_DESKTOP]: {
              margin: "0 auto 32px",
            },
          })}
        >
          <div
            className={css({
              display: "flex",
              justifyContent: "center",
              position: "relative",
            })}
            role="button"
            onClick={() => showVideo && setIsModalOpen(true)}
          >
            <Image
              customStyle={{
                maxWidth: 970,
                width: "100%",
                margin: "0 auto",
                filter: "drop-shadow(0px 10px 35px rgba(0, 0, 0, 0.03))",
              }}
              src={src}
              alt="Dashboard Screen"
            />
            {showVideo && (
              <StyledIconWrapper
                wrapperStyle={{
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <Icon iconType="PLAY-ICON-BIG" />
              </StyledIconWrapper>
            )}
          </div>
          {isModalOpen && (
            <Modal
              id="video-modal"
              onClose={() => setIsModalOpen(false)}
              customStyle={{
                height: "600px",
                maxHeight: "95vh",
                [bp.FROM_DESKTOP]: {
                  minWidth: 1000,
                  width: "50vw",
                },
              }}
            >
              <iframe
                className={css({
                  border: "none",
                })}
                width="100%"
                height="500px"
                src="https://www.youtube.com/embed/aF8ApO_lx2w?autoplay=1"
                title="Magicul Figma Backup Demo Video"
                allow="fullscreen; accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              />
            </Modal>
          )}
        </div>
      </div>
    </Grid>
  );
};
